/**
 * MilestoneCard — Design-system primitive (re-homed from `components/v2/`).
 *
 * WAVE 6 BATCH 2: Canonical home is now `design-system/components/`.
 * The legacy `components/v2/MilestoneCard.jsx` is retained as a thin shim
 * that re-exports this module so that deferred consumers (V2Preview)
 * continue to compile until they are migrated in a later batch.
 *
 * Communicates progress on a step within a longer journey. Three states:
 * completed / in_progress / upcoming. Optionally shows a completed date,
 * a "next step" hint, and a numeric metric.
 *
 * Test IDs (UNCHANGED):
 *   v2-milestone-card, v2-milestone-status, v2-milestone-title,
 *   v2-milestone-metric, v2-milestone-completed-date, v2-milestone-next-step
 */
import React from "react";
import clsx from "clsx";
import { CheckCircle2, CircleDashed, Loader2 } from "lucide-react";

const STATUS_CONFIG = {
  completed: {
    label: "Completed",
    Icon: CheckCircle2,
    chipBg:   "bg-[var(--ds-color-success-soft)]",
    chipText: "text-[var(--ds-color-success)]",
    ring:     "border-[var(--ds-color-border-strong)]",
  },
  in_progress: {
    label: "In progress",
    Icon: Loader2,
    chipBg:   "bg-[var(--ds-color-info-soft)]",
    chipText: "text-[var(--ds-color-info)]",
    ring:     "border-[var(--ds-color-border)]",
  },
  upcoming: {
    label: "Upcoming",
    Icon: CircleDashed,
    chipBg:   "bg-[var(--ds-color-surface-muted)]",
    chipText: "text-[var(--ds-color-ink-muted)]",
    ring:     "border-[var(--ds-color-border)]",
  },
};

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch (_) {
    return iso;
  }
}

export default function MilestoneCard({
  status = "upcoming",
  title,
  completedDate,
  nextStep,
  metric,
}) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.upcoming;
  const { Icon } = cfg;
  return (
    <article
      data-testid="v2-milestone-card"
      className={clsx(
        "rounded-[var(--ds-radius-lg)] bg-[var(--ds-color-surface)] border shadow-[var(--ds-shadow-card)] p-5 md:p-6 flex flex-col gap-3",
        cfg.ring
      )}
    >
      <div className="flex items-center justify-between">
        <span
          data-testid="v2-milestone-status"
          className={clsx(
            "inline-flex items-center gap-1.5 rounded-[var(--ds-radius-md)] px-2.5 py-1 text-[11px] uppercase font-semibold tracking-[0.12em]",
            cfg.chipBg,
            cfg.chipText
          )}
        >
          <Icon
            className={clsx("h-3.5 w-3.5", status === "in_progress" && "animate-spin")}
            aria-hidden="true"
          />
          {cfg.label}
        </span>
        {metric ? (
          <div
            data-testid="v2-milestone-metric"
            className="inline-flex items-baseline gap-1.5"
          >
            <span className="text-[18px] font-bold text-[var(--ds-color-ink)] tabular-nums">
              {metric.value}
            </span>
            <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)]">
              {metric.label}
            </span>
          </div>
        ) : null}
      </div>
      <h4
        data-testid="v2-milestone-title"
        className="text-[18px] leading-snug text-[var(--ds-color-ink)]"
        style={{ fontFamily: "var(--ds-font-display)", fontWeight: 600 }}
      >
        {title}
      </h4>
      {completedDate && status === "completed" ? (
        <div
          data-testid="v2-milestone-completed-date"
          className="text-[13px] text-[var(--ds-color-ink-subtle)]"
        >
          Completed {fmtDate(completedDate)}
        </div>
      ) : null}
      {nextStep ? (
        <div className="mt-2 pt-3 border-t border-[var(--ds-color-border)] flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-[0.12em] text-[var(--ds-color-ink-subtle)]">
            Next
          </span>
          <span
            data-testid="v2-milestone-next-step"
            className="text-[13px] text-[var(--ds-color-ink)]"
          >
            {nextStep}
          </span>
        </div>
      ) : null}
    </article>
  );
}
